import React, { useEffect, useState } from "react";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  X,
  Upload,
  ArrowUpDown,
  Users,
} from "lucide-react";

import "./AdminTeamManagement.css";

const API_URL = "http://localhost:5000";

const emptyForm = {
  name: "",
  designation: "",
  tagline: "",
  instagram: "",
  linkedin: "",
  facebook: "",
  displayOrder: 1,
  isActive: true,
};

function AdminTeamManagement() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);

  const [form, setForm] = useState({ ...emptyForm });
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState("");

  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  // ==========================================
  // FETCH TEAM MEMBERS
  // ==========================================

  const fetchMembers = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${API_URL}/api/team`);
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(
          result?.message || "Unable to load team members"
        );
      }

      setMembers(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      console.error("Fetch team error:", error);
      alert(error.message || "Unable to load team members");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  // ==========================================
  // FORM CHANGE
  // ==========================================

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ==========================================
  // IMAGE CHANGE
  // ==========================================

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select a valid image file.");
      e.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert("Image must be smaller than 5MB.");
      e.target.value = "";
      return;
    }

    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }

    const imageUrl = URL.createObjectURL(file);

    setImage(file);
    setPreview(imageUrl);
  };

  // ==========================================
  // OPEN ADD MODAL
  // ==========================================

  const openAddModal = () => {
    setEditingMember(null);

    setForm({
      ...emptyForm,
      displayOrder: members.length + 1,
      isActive: true,
    });

    setImage(null);
    setPreview("");
    setShowModal(true);
  };

  // ==========================================
  // OPEN EDIT MODAL
  // ==========================================

  const openEditModal = (member) => {
    setEditingMember(member);

    setForm({
      name: member.name || "",
      designation: member.designation || "",
      tagline: member.tagline || "",
      instagram: member.instagram || "",
      linkedin: member.linkedin || "",
      facebook: member.facebook || "",
      displayOrder: Number(member.displayOrder) || 1,
      isActive: member.isActive ?? true,
    });

    setImage(null);
    setPreview(member.image || "");
    setShowModal(true);
  };

  // ==========================================
  // CLOSE MODAL
  // ==========================================

  const closeModal = () => {
    if (saving) return;

    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }

    setShowModal(false);
    setEditingMember(null);
    setImage(null);
    setPreview("");
    setForm({ ...emptyForm });
  };

  // ==========================================
  // SUBMIT FORM
  // ==========================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      alert("Name is required.");
      return;
    }

    if (!form.designation.trim()) {
      alert("Designation is required.");
      return;
    }

    if (
      form.displayOrder === "" ||
      Number(form.displayOrder) < 1
    ) {
      alert("Display Order must be 1 or greater.");
      return;
    }

    if (!editingMember && !image) {
      alert("Please select team member photo.");
      return;
    }

    try {
      setSaving(true);

      const formData = new FormData();

      formData.append("name", form.name.trim());
      formData.append(
        "designation",
        form.designation.trim()
      );
      formData.append("tagline", form.tagline.trim());
      formData.append("instagram", form.instagram.trim());
      formData.append("linkedin", form.linkedin.trim());
      formData.append("facebook", form.facebook.trim());
      formData.append(
        "displayOrder",
        String(Number(form.displayOrder))
      );
      formData.append(
        "isActive",
        String(Boolean(form.isActive))
      );

      if (image) {
        formData.append("image", image);
      }

      const url = editingMember
        ? `${API_URL}/api/team/${editingMember._id}`
        : `${API_URL}/api/team`;

      const method = editingMember ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        body: formData,
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(
          result?.message || "Something went wrong."
        );
      }

      alert(
        editingMember
          ? "Team member updated successfully."
          : "Team member added successfully."
      );

      closeModal();
      await fetchMembers();
    } catch (error) {
      console.error("Save team member error:", error);
      alert(
        error.message || "Unable to save team member."
      );
    } finally {
      setSaving(false);
    }
  };

  // ==========================================
  // DELETE MEMBER
  // ==========================================

  const handleDelete = async (member) => {
    const confirmed = window.confirm(
      `Delete ${member.name}? This will also remove their Firebase image.`
    );

    if (!confirmed) return;

    try {
      const response = await fetch(
        `${API_URL}/api/team/${member._id}`,
        {
          method: "DELETE",
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(
          result?.message ||
            "Unable to delete team member."
        );
      }

      alert("Team member deleted successfully.");
      await fetchMembers();
    } catch (error) {
      console.error("Delete error:", error);
      alert(
        error.message || "Unable to delete team member."
      );
    }
  };

  // ==========================================
  // TOGGLE STATUS
  // ==========================================

  const toggleStatus = async (member) => {
    try {
      const response = await fetch(
        `${API_URL}/api/team/${member._id}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            isActive: !member.isActive,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(
          result?.message ||
            "Unable to update member status."
        );
      }

      await fetchMembers();
    } catch (error) {
      console.error("Status error:", error);
      alert(
        error.message || "Unable to update member status."
      );
    }
  };

  // ==========================================
  // FILTER + SORT
  // ==========================================

  const filteredMembers = members
    .filter((member) => {
      const text = `
        ${member.name || ""}
        ${member.designation || ""}
        ${member.tagline || ""}
      `.toLowerCase();

      return text.includes(search.toLowerCase());
    })
    .sort((a, b) => {
      const orderA = Number(a.displayOrder) || 0;
      const orderB = Number(b.displayOrder) || 0;

      if (orderA !== orderB) {
        return orderA - orderB;
      }

      return (
        new Date(a.createdAt || 0) -
        new Date(b.createdAt || 0)
      );
    });

  return (
    <div className="admin-team-page">

      {/* ==========================================
          HEADER
      ========================================== */}

      <div className="admin-team-header">
        <div>
          <div className="admin-team-eyebrow">
            <Users size={15} />
            CONTENT MANAGEMENT
          </div>

          <h1>Team Management</h1>

          <p>
            Manage your team members, profiles, social
            links and display order.
          </p>
        </div>

        <button
          type="button"
          className="team-add-btn"
          onClick={openAddModal}
        >
          <Plus size={19} />
          Add Team Member
        </button>
      </div>

      {/* ==========================================
          STATS
      ========================================== */}

      <div className="team-stats">

        {/* TOTAL */}

        <div className="team-stat-card">
          <div className="team-stat-icon">
            <Users size={21} />
          </div>

          <div>
            <span>Total Members</span>
            <strong>{members.length}</strong>
          </div>
        </div>

        {/* ACTIVE */}

        <div className="team-stat-card">
          <div className="team-stat-icon active">
            <Eye size={21} />
          </div>

          <div>
            <span>Active</span>
            <strong>
              {members.filter(
                (member) => member.isActive
              ).length}
            </strong>
          </div>
        </div>

        {/* HIDDEN */}

        <div className="team-stat-card">
          <div className="team-stat-icon inactive">
            <EyeOff size={21} />
          </div>

          <div>
            <span>Hidden</span>
            <strong>
              {members.filter(
                (member) => !member.isActive
              ).length}
            </strong>
          </div>
        </div>

      </div>

      {/* ==========================================
          TOOLBAR
      ========================================== */}

      <div className="team-toolbar">

        <div className="team-search">
          <Search size={18} />

          <input
            type="text"
            placeholder="Search team members..."
            value={search}
            onChange={(e) =>
              setSearch(e.target.value)
            }
          />
        </div>

        <div className="team-order-info">
          <ArrowUpDown size={16} />
          Ordered by Display Order
        </div>

      </div>

      {/* ==========================================
          TABLE
      ========================================== */}

      <div className="team-table-wrapper">

        <table className="team-table">

          <thead>
            <tr>
              <th>ORDER</th>
              <th>MEMBER</th>
              <th>DESIGNATION</th>
              <th>SOCIAL</th>
              <th>STATUS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>

          <tbody>

            {/* LOADING */}

            {loading ? (
              <tr>
                <td colSpan="6">
                  <div className="team-loading">
                    Loading team members...
                  </div>
                </td>
              </tr>

            ) : filteredMembers.length === 0 ? (

              /* EMPTY */

              <tr>
                <td colSpan="6">
                  <div className="team-empty">

                    <Users size={35} />

                    <h3>
                      No team members found
                    </h3>

                    <p>
                      {search
                        ? "Try a different search term."
                        : "Add your first team member to get started."}
                    </p>

                  </div>
                </td>
              </tr>

            ) : (

              /* MEMBERS */

              filteredMembers.map((member) => (

                <tr key={member._id}>

                  {/* ORDER */}

                  <td>
                    <div className="team-order">
                      #
                      {String(
                        member.displayOrder || 0
                      ).padStart(2, "0")}
                    </div>
                  </td>

                  {/* MEMBER */}

                  <td>
                    <div className="team-member-cell">

                      <img
                        src={member.image}
                        alt={member.name}
                        onError={(e) => {
                          e.currentTarget.style.opacity = "0";
                        }}
                      />

                      <div>
                        <strong>
                          {member.name}
                        </strong>

                        {member.tagline && (
                          <span>
                            {member.tagline}
                          </span>
                        )}
                      </div>

                    </div>
                  </td>

                  {/* DESIGNATION */}

                  <td>
                    <div className="team-designation">
                      {member.designation}
                    </div>
                  </td>

                  {/* SOCIAL */}

                  <td>
                    <div className="team-social-mini">

                      {member.instagram && (
                        <a
                          href={member.instagram}
                          target="_blank"
                          rel="noreferrer"
                          title="Instagram"
                          className="social-badge"
                        >
                          IG
                        </a>
                      )}

                      {member.linkedin && (
                        <a
                          href={member.linkedin}
                          target="_blank"
                          rel="noreferrer"
                          title="LinkedIn"
                          className="social-badge"
                        >
                          in
                        </a>
                      )}

                      {member.facebook && (
                        <a
                          href={member.facebook}
                          target="_blank"
                          rel="noreferrer"
                          title="Facebook"
                          className="social-badge"
                        >
                          f
                        </a>
                      )}

                    </div>
                  </td>

                  {/* STATUS */}

                  <td>
                    <button
                      type="button"
                      className={`team-status ${
                        member.isActive
                          ? "is-active"
                          : "is-inactive"
                      }`}
                      onClick={() =>
                        toggleStatus(member)
                      }
                    >
                      {member.isActive ? (
                        <>
                          <Eye size={14} />
                          Active
                        </>
                      ) : (
                        <>
                          <EyeOff size={14} />
                          Hidden
                        </>
                      )}
                    </button>
                  </td>

                  {/* ACTIONS */}

                  <td>
                    <div className="team-actions">

                      <button
                        type="button"
                        className="team-action edit"
                        onClick={() =>
                          openEditModal(member)
                        }
                        title="Edit"
                      >
                        <Pencil size={17} />
                      </button>

                      <button
                        type="button"
                        className="team-action delete"
                        onClick={() =>
                          handleDelete(member)
                        }
                        title="Delete"
                      >
                        <Trash2 size={17} />
                      </button>

                    </div>
                  </td>

                </tr>

              ))
            )}

          </tbody>

        </table>

      </div>

      {/* ==========================================
          MODAL
      ========================================== */}

      {showModal && (
        <div
          className="team-modal-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              closeModal();
            }
          }}
        >

          <div className="team-modal">

            {/* MODAL HEADER */}

            <div className="team-modal-header">

              <div>

                <span>
                  {editingMember
                    ? "EDIT TEAM MEMBER"
                    : "NEW TEAM MEMBER"}
                </span>

                <h2>
                  {editingMember
                    ? "Update Profile"
                    : "Add Team Member"}
                </h2>

              </div>

              <button
                type="button"
                className="team-modal-close"
                onClick={closeModal}
                disabled={saving}
              >
                <X size={20} />
              </button>

            </div>

            {/* FORM */}

            <form
              className="team-form"
              onSubmit={handleSubmit}
            >

              {/* ==========================================
                  PHOTO
              ========================================== */}

              <div className="team-photo-section">

                <label>
                  Profile Photo
                </label>

                <div className="team-photo-upload">

                  {preview ? (

                    <img
                      src={preview}
                      alt="Preview"
                    />

                  ) : (

                    <div className="team-photo-placeholder">
                      <Upload size={28} />
                      <span>
                        Upload Photo
                      </span>
                    </div>

                  )}

                  <label className="team-photo-button">

                    <Upload size={16} />

                    Choose Photo

                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleImageChange}
                    />

                  </label>

                </div>

                <small>
                  JPG, PNG or WEBP · Maximum 5MB
                </small>

              </div>

              {/* ==========================================
                  BASIC INFO
              ========================================== */}

              <div className="team-form-grid">

                {/* NAME */}

                <div className="team-field">

                  <label>
                    Full Name <b>*</b>
                  </label>

                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="e.g. MALIKA DANG"
                    required
                  />

                </div>

                {/* DESIGNATION */}

                <div className="team-field">

                  <label>
                    Designation <b>*</b>
                  </label>

                  <input
                    type="text"
                    name="designation"
                    value={form.designation}
                    onChange={handleChange}
                    placeholder="e.g. CEO, Co-founder"
                    required
                  />

                </div>

              </div>

              {/* ==========================================
                  TAGLINE
              ========================================== */}

              <div className="team-field">

                <label>
                  Tagline
                </label>

                <input
                  type="text"
                  name="tagline"
                  value={form.tagline}
                  onChange={handleChange}
                  placeholder="e.g. Destroyer of Mediocrity"
                />

              </div>

              {/* ==========================================
                  SOCIAL PROFILES
              ========================================== */}

              <div className="team-social-heading">
                SOCIAL PROFILES
              </div>

              <div className="team-form-grid">

                {/* INSTAGRAM */}

                <div className="team-field">

                  <label>
                    Instagram
                  </label>

                  <input
                    type="url"
                    name="instagram"
                    value={form.instagram}
                    onChange={handleChange}
                    placeholder="https://instagram.com/..."
                  />

                </div>

                {/* LINKEDIN */}

                <div className="team-field">

                  <label>
                    LinkedIn
                  </label>

                  <input
                    type="url"
                    name="linkedin"
                    value={form.linkedin}
                    onChange={handleChange}
                    placeholder="https://linkedin.com/in/..."
                  />

                </div>

                {/* FACEBOOK */}

                <div className="team-field">

                  <label>
                    Facebook
                  </label>

                  <input
                    type="url"
                    name="facebook"
                    value={form.facebook}
                    onChange={handleChange}
                    placeholder="https://facebook.com/..."
                  />

                </div>

              </div>

              {/* ==========================================
                  ORDER + VISIBILITY
              ========================================== */}

              <div className="team-form-grid">

                {/* DISPLAY ORDER */}

                <div className="team-field">

                  <label>
                    Display Order <b>*</b>
                  </label>

                  <input
                    type="number"
                    min="1"
                    step="1"
                    name="displayOrder"
                    value={form.displayOrder}
                    onChange={handleChange}
                    required
                  />

                  <small>
                    Lower number appears first.
                  </small>

                </div>

                {/* VISIBILITY */}

                <div className="team-field">

                  <label>
                    Visibility
                  </label>

                  <label className="team-switch-row">

                    <input
                      type="checkbox"
                      name="isActive"
                      checked={form.isActive}
                      onChange={handleChange}
                    />

                    <span className="team-switch"></span>

                    <span>
                      {form.isActive
                        ? "Visible on website"
                        : "Hidden from website"}
                    </span>

                  </label>

                </div>

              </div>

              {/* ==========================================
                  FOOTER
              ========================================== */}

              <div className="team-modal-footer">

                <button
                  type="button"
                  className="team-cancel-btn"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="team-save-btn"
                  disabled={saving}
                >
                  {saving
                    ? "Saving..."
                    : editingMember
                    ? "Save Changes"
                    : "Add Member"}
                </button>

              </div>

            </form>

          </div>

        </div>
      )}

    </div>
  );
}

export default AdminTeamManagement;